import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import type {
  ApplicantData,
  EmploymentData,
  AssetsData,
  LiabilitiesData,
  IncomeData,
  ExpensesData,
  RequirementsData,
} from "@/types/fact-finder";

interface FactFinderData {
  id: string;
  status: string;
  applicant1: ApplicantData | null;
  applicant2: ApplicantData | null;
  employment: EmploymentData | null;
  assets: AssetsData | null;
  liabilities: LiabilitiesData | null;
  income: IncomeData | null;
  expenses: ExpensesData | null;
  requirements: RequirementsData | null;
  createdAt: string;
  updatedAt: string;
}

const formatCurrency = (value: number | undefined | null): string => {
  if (value === undefined || value === null) return "";
  return `$${value.toLocaleString()}`;
};

const formatDate = (date: string): string => {
  return new Date(date).toLocaleDateString("en-AU", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

// PDF Export
export function exportToPDF(data: FactFinderData): void {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  let y = 20;

  // Title
  doc.setFontSize(20);
  doc.setFont("helvetica", "bold");
  doc.text("Fact Finder Report", pageWidth / 2, y, { align: "center" });
  y += 10;

  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  doc.text(`ID: ${data.id}`, pageWidth / 2, y, { align: "center" });
  y += 5;
  doc.text(`Status: ${data.status.toUpperCase()}`, pageWidth / 2, y, { align: "center" });
  y += 5;
  doc.text(`Generated: ${formatDate(new Date().toISOString())}`, pageWidth / 2, y, { align: "center" });
  y += 15;

  // Helper to add section title
  const addSectionTitle = (title: string): void => {
    if (y > 260) {
      doc.addPage();
      y = 20;
    }
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(0, 80, 157);
    doc.text(title, 14, y);
    y += 8;
    doc.setTextColor(0, 0, 0);
  };

  // Helper to add table
  const addTable = (headers: string[], rows: string[][]): void => {
    autoTable(doc, {
      startY: y,
      head: [headers],
      body: rows,
      theme: "striped",
      styles: { fontSize: 9 },
      headStyles: { fillColor: [0, 80, 157] },
      margin: { left: 14, right: 14 },
    });
    y = (doc as unknown as { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 10;
  };

  // Applicant 1
  if (data.applicant1) {
    addSectionTitle("Applicant 1 - Personal Details");
    const { personal, currentAddress } = data.applicant1;
    addTable(
      ["Field", "Value"],
      [
        ["Full Name", `${personal.title} ${personal.firstName} ${personal.middleNames || ""} ${personal.surname}`.trim()],
        ["Email", personal.email || ""],
        ["Mobile", personal.mobile || ""],
        ["Date of Birth", personal.dateOfBirth || ""],
        ["Marital Status", personal.maritalStatus?.replace("_", " ") || ""],
        ["Dependants", String(personal.numberOfDependants || 0)],
        ["First Home Buyer", personal.isFirstHomeBuyer ? "Yes" : "No"],
        ["Permanent Resident", personal.isPermanentResident ? "Yes" : "No"],
      ]
    );

    addSectionTitle("Applicant 1 - Current Address");
    addTable(
      ["Field", "Value"],
      [
        ["Address", currentAddress.address || ""],
        ["Suburb", currentAddress.suburb || ""],
        ["State", currentAddress.state || ""],
        ["Postcode", currentAddress.postcode || ""],
        ["Residency Type", currentAddress.residencyType?.replace("_", " ") || ""],
        ["Rent per Week", currentAddress.rentPerWeek ? formatCurrency(currentAddress.rentPerWeek) : "N/A"],
        ["Date Moved In", currentAddress.dateMovedIn || ""],
      ]
    );
  }

  // Applicant 2
  if (data.applicant2) {
    addSectionTitle("Applicant 2 - Personal Details");
    const { personal, currentAddress } = data.applicant2;
    addTable(
      ["Field", "Value"],
      [
        ["Full Name", `${personal.title} ${personal.firstName} ${personal.middleNames || ""} ${personal.surname}`.trim()],
        ["Email", personal.email || ""],
        ["Mobile", personal.mobile || ""],
        ["Date of Birth", personal.dateOfBirth || ""],
        ["Marital Status", personal.maritalStatus?.replace("_", " ") || ""],
        ["Dependants", String(personal.numberOfDependants || 0)],
        ["First Home Buyer", personal.isFirstHomeBuyer ? "Yes" : "No"],
        ["Permanent Resident", personal.isPermanentResident ? "Yes" : "No"],
      ]
    );

    addSectionTitle("Applicant 2 - Current Address");
    addTable(
      ["Field", "Value"],
      [
        ["Address", currentAddress.address || ""],
        ["Suburb", currentAddress.suburb || ""],
        ["State", currentAddress.state || ""],
        ["Postcode", currentAddress.postcode || ""],
        ["Residency Type", currentAddress.residencyType?.replace("_", " ") || ""],
        ["Rent per Week", currentAddress.rentPerWeek ? formatCurrency(currentAddress.rentPerWeek) : "N/A"],
        ["Date Moved In", currentAddress.dateMovedIn || ""],
      ]
    );
  }

  // Employment
  if (data.employment) {
    const renderEmployment = (emp: typeof data.employment.applicant1, label: string) => {
      addSectionTitle(`${label} - Current Employment`);
      addTable(
        ["Field", "Value"],
        [
          ["Employment Type", emp.current.employmentType?.replace("_", " ") || ""],
          ["Employer", emp.current.employerName || ""],
          ["Occupation", emp.current.occupation || ""],
          ["Start Date", emp.current.startDate || ""],
          ["Employer Type", emp.current.employerType?.replace("_", " ") || ""],
          ["Address", `${emp.current.address || ""}, ${emp.current.suburb || ""} ${emp.current.state || ""} ${emp.current.postcode || ""}`.trim()],
          ["Contact", `${emp.current.contactPerson || ""} - ${emp.current.contactPhone || ""}`],
          ["On Probation", emp.current.isOnProbation ? `Yes (${emp.current.probationRemaining})` : "No"],
        ]
      );

      if (emp.previous && emp.previous.length > 0) {
        emp.previous.forEach((prev, idx) => {
          addSectionTitle(`${label} - Previous Employment ${idx + 1}`);
          addTable(
            ["Field", "Value"],
            [
              ["Employer", prev.employerName || ""],
              ["Occupation", prev.occupation || ""],
              ["Start Date", prev.startDate || ""],
              ["End Date", prev.endDate || ""],
            ]
          );
        });
      }
    };

    renderEmployment(data.employment.applicant1, "Applicant 1");
    if (data.employment.applicant2) {
      renderEmployment(data.employment.applicant2, "Applicant 2");
    }
  }

  // Assets
  if (data.assets) {
    addSectionTitle("Assets");
    const totalAssets =
      data.assets.properties.reduce((sum, p) => sum + (p.currentValue || 0), 0) +
      data.assets.savings.reduce((sum, s) => sum + (s.amount || 0), 0) +
      data.assets.shares.reduce((sum, s) => sum + (s.value || 0), 0) +
      data.assets.superannuation.reduce((sum, s) => sum + (s.value || 0), 0) +
      data.assets.vehicles.reduce((sum, v) => sum + (v.value || 0), 0);

    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.text(`Total Assets: ${formatCurrency(totalAssets)}`, 14, y);
    y += 10;

    if (data.assets.properties.length > 0) {
      addTable(
        ["Property Address", "Type", "Value", "Loan Balance"],
        data.assets.properties.map((p) => [
          p.address || "",
          p.propertyType || "",
          formatCurrency(p.currentValue),
          formatCurrency(p.loanBalance),
        ])
      );
    }

    if (data.assets.savings.length > 0) {
      addTable(
        ["Institution", "Account Type", "Amount"],
        data.assets.savings.map((s) => [
          s.bankInstitution || "",
          s.accountType || "",
          formatCurrency(s.amount),
        ])
      );
    }

    if (data.assets.vehicles.length > 0) {
      addTable(
        ["Vehicle", "Value"],
        data.assets.vehicles.map((v) => [
          `${v.year || ""} ${v.make || ""} ${v.model || ""}`.trim(),
          formatCurrency(v.value),
        ])
      );
    }
  }

  // Liabilities
  if (data.liabilities) {
    addSectionTitle("Liabilities");
    const totalLoans = data.liabilities.loans.reduce((sum, l) => sum + (l.amountOwing || 0), 0);
    const totalCards = data.liabilities.creditCards.reduce((sum, c) => sum + (c.amountOwing || 0), 0);

    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.text(`Total Liabilities: ${formatCurrency(totalLoans + totalCards)}`, 14, y);
    y += 10;

    if (data.liabilities.loans.length > 0) {
      addTable(
        ["Loan Type", "Lender", "Amount Owing", "Repayment"],
        data.liabilities.loans.map((l) => [
          l.loanType || "",
          l.lender || "",
          formatCurrency(l.amountOwing),
          `${formatCurrency(l.repaymentAmount)} ${l.repaymentFrequency || ""}`,
        ])
      );
    }

    if (data.liabilities.creditCards.length > 0) {
      addTable(
        ["Card Type", "Lender", "Limit", "Owing"],
        data.liabilities.creditCards.map((c) => [
          c.cardType || "",
          c.lender || "",
          formatCurrency(c.limit),
          formatCurrency(c.amountOwing),
        ])
      );
    }
  }

  // Income
  if (data.income) {
    addSectionTitle("Income");
    const totalIncome =
      (data.income.applicant1Salary || 0) +
      (data.income.applicant2Salary || 0) +
      (data.income.businessProfit || 0) +
      (data.income.investmentIncome || 0) +
      (data.income.permanentPensions || 0) +
      (data.income.otherIncome || 0);

    addTable(
      ["Income Source", "Annual Amount"],
      [
        ["Applicant 1 Salary", formatCurrency(data.income.applicant1Salary)],
        ["Applicant 2 Salary", formatCurrency(data.income.applicant2Salary)],
        ["Business Profit", formatCurrency(data.income.businessProfit)],
        ["Investment Income", formatCurrency(data.income.investmentIncome)],
        ["Pensions", formatCurrency(data.income.permanentPensions)],
        ["Other Income", formatCurrency(data.income.otherIncome)],
        ["TOTAL", formatCurrency(totalIncome)],
      ]
    );
  }

  // Expenses
  if (data.expenses) {
    addSectionTitle("Monthly Expenses");
    addTable(
      ["Category", "Amount"],
      [
        ["Groceries", formatCurrency(data.expenses.groceries)],
        ["Personal", formatCurrency(data.expenses.personal)],
        ["Property (Owner Occupied)", formatCurrency(data.expenses.propertyOwnerOccupied)],
        ["Property (Investment)", formatCurrency(data.expenses.propertyInvestment)],
        ["Education", formatCurrency(data.expenses.education)],
        ["Child Care", formatCurrency(data.expenses.childCare)],
        ["Health", formatCurrency(data.expenses.health)],
        ["Transport", formatCurrency(data.expenses.transport)],
        ["Insurance", formatCurrency(data.expenses.insurance)],
        ["Entertainment", formatCurrency(data.expenses.entertainment)],
        ["Communication", formatCurrency(data.expenses.communication)],
        ["Child Support", formatCurrency(data.expenses.childSupport)],
        ["Other", formatCurrency(data.expenses.other)],
        ["TOTAL MONTHLY", formatCurrency(data.expenses.totalMonthly)],
      ]
    );
  }

  // Requirements
  if (data.requirements) {
    addSectionTitle("Requirements & Objectives");
    addTable(
      ["Field", "Value"],
      [
        ["Primary Reason", data.requirements.primaryReason || ""],
        ["Contract Length", data.requirements.expectedContractLength?.replace("_", " ") || ""],
        ["Seeking Interest Only", data.requirements.seekingInterestOnly ? "Yes" : "No"],
        ["Rate Concern", data.requirements.concernAboutRisingRates?.replace("_", " ") || ""],
        ["Has Defaults/Judgements", data.requirements.hasDefaultsOrJudgements ? "Yes" : "No"],
        ["All Commitments Up to Date", data.requirements.allCommitmentsUpToDate ? "Yes" : "No"],
        ["Expects Financial Changes", data.requirements.expectsFinancialChanges ? "Yes" : "No"],
        ["Wants Financial Adviser Intro", data.requirements.wantsFinancialAdviserIntro ? "Yes" : "No"],
      ]
    );

    const features = Object.entries(data.requirements.desiredFeatures || {})
      .filter(([, v]) => v)
      .map(([k]) => k.replace(/([A-Z])/g, " $1").trim())
      .join(", ");

    if (features) {
      doc.setFontSize(10);
      doc.text(`Desired Features: ${features}`, 14, y);
      y += 10;
    }
  }

  // Footer
  const pageCount = doc.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(128);
    doc.text(
      `Page ${i} of ${pageCount} | Path Financial Solutions | Confidential`,
      pageWidth / 2,
      doc.internal.pageSize.getHeight() - 10,
      { align: "center" }
    );
  }

  // Save
  const fileName = `FactFinder_${data.id.substring(0, 8)}_${formatDate(new Date().toISOString()).replace(/\//g, "-")}.pdf`;
  doc.save(fileName);
}

// CSV Export
export function exportToCSV(data: FactFinderData): void {
  const rows: Record<string, string | number>[] = [];

  // Summary row
  rows.push({
    Section: "Summary",
    Field: "Fact Finder ID",
    Value: data.id,
  });
  rows.push({
    Section: "Summary",
    Field: "Status",
    Value: data.status,
  });
  rows.push({
    Section: "Summary",
    Field: "Created Date",
    Value: formatDate(data.createdAt),
  });

  // Applicant 1
  if (data.applicant1) {
    const { personal, currentAddress } = data.applicant1;
    rows.push({ Section: "Applicant 1", Field: "Full Name", Value: `${personal.title} ${personal.firstName} ${personal.surname}` });
    rows.push({ Section: "Applicant 1", Field: "Email", Value: personal.email || "" });
    rows.push({ Section: "Applicant 1", Field: "Mobile", Value: personal.mobile || "" });
    rows.push({ Section: "Applicant 1", Field: "Date of Birth", Value: personal.dateOfBirth || "" });
    rows.push({ Section: "Applicant 1", Field: "Address", Value: `${currentAddress.address}, ${currentAddress.suburb} ${currentAddress.state} ${currentAddress.postcode}` });
  }

  // Applicant 2
  if (data.applicant2) {
    const { personal, currentAddress } = data.applicant2;
    rows.push({ Section: "Applicant 2", Field: "Full Name", Value: `${personal.title} ${personal.firstName} ${personal.surname}` });
    rows.push({ Section: "Applicant 2", Field: "Email", Value: personal.email || "" });
    rows.push({ Section: "Applicant 2", Field: "Mobile", Value: personal.mobile || "" });
    rows.push({ Section: "Applicant 2", Field: "Date of Birth", Value: personal.dateOfBirth || "" });
    rows.push({ Section: "Applicant 2", Field: "Address", Value: `${currentAddress.address}, ${currentAddress.suburb} ${currentAddress.state} ${currentAddress.postcode}` });
  }

  // Employment
  if (data.employment) {
    const { applicant1, applicant2 } = data.employment;
    rows.push({ Section: "Employment - Applicant 1", Field: "Employer", Value: applicant1.current.employerName || "" });
    rows.push({ Section: "Employment - Applicant 1", Field: "Occupation", Value: applicant1.current.occupation || "" });
    rows.push({ Section: "Employment - Applicant 1", Field: "Start Date", Value: applicant1.current.startDate || "" });
    if (applicant2) {
      rows.push({ Section: "Employment - Applicant 2", Field: "Employer", Value: applicant2.current.employerName || "" });
      rows.push({ Section: "Employment - Applicant 2", Field: "Occupation", Value: applicant2.current.occupation || "" });
      rows.push({ Section: "Employment - Applicant 2", Field: "Start Date", Value: applicant2.current.startDate || "" });
    }
  }

  // Assets
  if (data.assets) {
    data.assets.properties.forEach((p, i) => {
      rows.push({ Section: `Assets - Property ${i + 1}`, Field: "Address", Value: p.address || "" });
      rows.push({ Section: `Assets - Property ${i + 1}`, Field: "Value", Value: p.currentValue || 0 });
      rows.push({ Section: `Assets - Property ${i + 1}`, Field: "Loan Balance", Value: p.loanBalance || 0 });
    });
    data.assets.savings.forEach((s, i) => {
      rows.push({ Section: `Assets - Savings ${i + 1}`, Field: "Institution", Value: s.bankInstitution || "" });
      rows.push({ Section: `Assets - Savings ${i + 1}`, Field: "Amount", Value: s.amount || 0 });
    });
  }

  // Liabilities
  if (data.liabilities) {
    data.liabilities.loans.forEach((l, i) => {
      rows.push({ Section: `Liabilities - Loan ${i + 1}`, Field: "Type", Value: l.loanType || "" });
      rows.push({ Section: `Liabilities - Loan ${i + 1}`, Field: "Lender", Value: l.lender || "" });
      rows.push({ Section: `Liabilities - Loan ${i + 1}`, Field: "Amount Owing", Value: l.amountOwing || 0 });
    });
    data.liabilities.creditCards.forEach((c, i) => {
      rows.push({ Section: `Liabilities - Credit Card ${i + 1}`, Field: "Type", Value: c.cardType || "" });
      rows.push({ Section: `Liabilities - Credit Card ${i + 1}`, Field: "Limit", Value: c.limit || 0 });
      rows.push({ Section: `Liabilities - Credit Card ${i + 1}`, Field: "Owing", Value: c.amountOwing || 0 });
    });
  }

  // Income
  if (data.income) {
    rows.push({ Section: "Income", Field: "Applicant 1 Salary", Value: data.income.applicant1Salary || 0 });
    rows.push({ Section: "Income", Field: "Applicant 2 Salary", Value: data.income.applicant2Salary || 0 });
    rows.push({ Section: "Income", Field: "Business Profit", Value: data.income.businessProfit || 0 });
    rows.push({ Section: "Income", Field: "Investment Income", Value: data.income.investmentIncome || 0 });
    rows.push({ Section: "Income", Field: "Pensions", Value: data.income.permanentPensions || 0 });
    rows.push({ Section: "Income", Field: "Other Income", Value: data.income.otherIncome || 0 });
  }

  // Expenses
  if (data.expenses) {
    rows.push({ Section: "Expenses", Field: "Groceries", Value: data.expenses.groceries || 0 });
    rows.push({ Section: "Expenses", Field: "Personal", Value: data.expenses.personal || 0 });
    rows.push({ Section: "Expenses", Field: "Property Owner Occupied", Value: data.expenses.propertyOwnerOccupied || 0 });
    rows.push({ Section: "Expenses", Field: "Property Investment", Value: data.expenses.propertyInvestment || 0 });
    rows.push({ Section: "Expenses", Field: "Education", Value: data.expenses.education || 0 });
    rows.push({ Section: "Expenses", Field: "Child Care", Value: data.expenses.childCare || 0 });
    rows.push({ Section: "Expenses", Field: "Health", Value: data.expenses.health || 0 });
    rows.push({ Section: "Expenses", Field: "Transport", Value: data.expenses.transport || 0 });
    rows.push({ Section: "Expenses", Field: "Insurance", Value: data.expenses.insurance || 0 });
    rows.push({ Section: "Expenses", Field: "Entertainment", Value: data.expenses.entertainment || 0 });
    rows.push({ Section: "Expenses", Field: "Communication", Value: data.expenses.communication || 0 });
    rows.push({ Section: "Expenses", Field: "Child Support", Value: data.expenses.childSupport || 0 });
    rows.push({ Section: "Expenses", Field: "Total Monthly", Value: data.expenses.totalMonthly || 0 });
  }

  // Requirements
  if (data.requirements) {
    rows.push({ Section: "Requirements", Field: "Primary Reason", Value: data.requirements.primaryReason || "" });
    rows.push({ Section: "Requirements", Field: "Contract Length", Value: data.requirements.expectedContractLength || "" });
    rows.push({ Section: "Requirements", Field: "Interest Only", Value: data.requirements.seekingInterestOnly ? "Yes" : "No" });
    rows.push({ Section: "Requirements", Field: "Rate Concern", Value: data.requirements.concernAboutRisingRates || "" });
  }

  const worksheet = XLSX.utils.json_to_sheet(rows);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Fact Finder");

  const fileName = `FactFinder_${data.id.substring(0, 8)}_${formatDate(new Date().toISOString()).replace(/\//g, "-")}.csv`;
  XLSX.writeFile(workbook, fileName, { bookType: "csv" });
}

// Excel Export
export function exportToExcel(data: FactFinderData): void {
  const workbook = XLSX.utils.book_new();

  // Summary sheet
  const summaryData = [
    ["Fact Finder Report"],
    ["ID", data.id],
    ["Status", data.status],
    ["Created", formatDate(data.createdAt)],
    ["Updated", formatDate(data.updatedAt)],
    ["Generated", formatDate(new Date().toISOString())],
  ];
  const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
  XLSX.utils.book_append_sheet(workbook, summarySheet, "Summary");

  // Applicants sheet
  if (data.applicant1 || data.applicant2) {
    const applicantRows = [
      ["Field", "Applicant 1", "Applicant 2"],
    ];

    const a1 = data.applicant1;
    const a2 = data.applicant2;

    applicantRows.push(["Full Name", a1 ? `${a1.personal.title} ${a1.personal.firstName} ${a1.personal.surname}` : "", a2 ? `${a2.personal.title} ${a2.personal.firstName} ${a2.personal.surname}` : ""]);
    applicantRows.push(["Email", a1?.personal.email || "", a2?.personal.email || ""]);
    applicantRows.push(["Mobile", a1?.personal.mobile || "", a2?.personal.mobile || ""]);
    applicantRows.push(["Date of Birth", a1?.personal.dateOfBirth || "", a2?.personal.dateOfBirth || ""]);
    applicantRows.push(["Marital Status", a1?.personal.maritalStatus || "", a2?.personal.maritalStatus || ""]);
    applicantRows.push(["Dependants", String(a1?.personal.numberOfDependants || 0), String(a2?.personal.numberOfDependants || 0)]);
    applicantRows.push(["First Home Buyer", a1?.personal.isFirstHomeBuyer ? "Yes" : "No", a2?.personal.isFirstHomeBuyer ? "Yes" : "No"]);
    applicantRows.push(["Current Address", a1 ? `${a1.currentAddress.address}, ${a1.currentAddress.suburb} ${a1.currentAddress.state} ${a1.currentAddress.postcode}` : "", a2 ? `${a2.currentAddress.address}, ${a2.currentAddress.suburb} ${a2.currentAddress.state} ${a2.currentAddress.postcode}` : ""]);
    applicantRows.push(["Residency Type", a1?.currentAddress.residencyType || "", a2?.currentAddress.residencyType || ""]);

    const applicantSheet = XLSX.utils.aoa_to_sheet(applicantRows);
    XLSX.utils.book_append_sheet(workbook, applicantSheet, "Applicants");
  }

  // Employment sheet
  if (data.employment) {
    const empRows = [["Field", "Applicant 1", "Applicant 2"]];
    const e1 = data.employment.applicant1;
    const e2 = data.employment.applicant2;

    empRows.push(["Employment Type", e1.current.employmentType, e2?.current.employmentType || ""]);
    empRows.push(["Employer", e1.current.employerName, e2?.current.employerName || ""]);
    empRows.push(["Occupation", e1.current.occupation, e2?.current.occupation || ""]);
    empRows.push(["Start Date", e1.current.startDate, e2?.current.startDate || ""]);
    empRows.push(["Employer Type", e1.current.employerType, e2?.current.employerType || ""]);
    empRows.push(["On Probation", e1.current.isOnProbation ? "Yes" : "No", e2?.current.isOnProbation ? "Yes" : "No"]);

    const empSheet = XLSX.utils.aoa_to_sheet(empRows);
    XLSX.utils.book_append_sheet(workbook, empSheet, "Employment");
  }

  // Assets sheet
  if (data.assets) {
    const assetRows: (string | number)[][] = [["Type", "Description", "Value", "Additional Info"]];

    data.assets.properties.forEach((p) => {
      assetRows.push(["Property", p.address || "", p.currentValue || 0, `Loan: ${formatCurrency(p.loanBalance)}`]);
    });
    data.assets.savings.forEach((s) => {
      assetRows.push(["Savings", s.bankInstitution || "", s.amount || 0, s.accountType || ""]);
    });
    data.assets.shares.forEach((s) => {
      assetRows.push(["Shares", s.heldWith || "", s.value || 0, ""]);
    });
    data.assets.superannuation.forEach((s) => {
      assetRows.push(["Superannuation", s.company || "", s.value || 0, ""]);
    });
    data.assets.vehicles.forEach((v) => {
      assetRows.push(["Vehicle", `${v.year} ${v.make} ${v.model}`, v.value || 0, ""]);
    });

    const totalAssets =
      data.assets.properties.reduce((sum, p) => sum + (p.currentValue || 0), 0) +
      data.assets.savings.reduce((sum, s) => sum + (s.amount || 0), 0) +
      data.assets.shares.reduce((sum, s) => sum + (s.value || 0), 0) +
      data.assets.superannuation.reduce((sum, s) => sum + (s.value || 0), 0) +
      data.assets.vehicles.reduce((sum, v) => sum + (v.value || 0), 0);

    assetRows.push(["", "", "", ""]);
    assetRows.push(["TOTAL ASSETS", "", totalAssets, ""]);

    const assetSheet = XLSX.utils.aoa_to_sheet(assetRows);
    XLSX.utils.book_append_sheet(workbook, assetSheet, "Assets");
  }

  // Liabilities sheet
  if (data.liabilities) {
    const liabRows: (string | number)[][] = [["Type", "Lender", "Amount Owing", "Repayment"]];

    data.liabilities.loans.forEach((l) => {
      liabRows.push([l.loanType || "", l.lender || "", l.amountOwing || 0, `${formatCurrency(l.repaymentAmount)} ${l.repaymentFrequency}`]);
    });
    data.liabilities.creditCards.forEach((c) => {
      liabRows.push([`Credit Card - ${c.cardType}`, c.lender || "", c.amountOwing || 0, `Limit: ${formatCurrency(c.limit)}`]);
    });

    const totalLiab =
      data.liabilities.loans.reduce((sum, l) => sum + (l.amountOwing || 0), 0) +
      data.liabilities.creditCards.reduce((sum, c) => sum + (c.amountOwing || 0), 0);

    liabRows.push(["", "", "", ""]);
    liabRows.push(["TOTAL LIABILITIES", "", totalLiab, ""]);

    const liabSheet = XLSX.utils.aoa_to_sheet(liabRows);
    XLSX.utils.book_append_sheet(workbook, liabSheet, "Liabilities");
  }

  // Income sheet
  if (data.income) {
    const incomeRows: (string | number)[][] = [["Source", "Annual Amount"]];
    incomeRows.push(["Applicant 1 Salary", data.income.applicant1Salary || 0]);
    incomeRows.push(["Applicant 2 Salary", data.income.applicant2Salary || 0]);
    incomeRows.push(["Business Profit", data.income.businessProfit || 0]);
    incomeRows.push(["Investment Income", data.income.investmentIncome || 0]);
    incomeRows.push(["Pensions", data.income.permanentPensions || 0]);
    incomeRows.push(["Other Income", data.income.otherIncome || 0]);
    incomeRows.push(["", ""]);

    const totalIncome =
      (data.income.applicant1Salary || 0) +
      (data.income.applicant2Salary || 0) +
      (data.income.businessProfit || 0) +
      (data.income.investmentIncome || 0) +
      (data.income.permanentPensions || 0) +
      (data.income.otherIncome || 0);
    incomeRows.push(["TOTAL ANNUAL INCOME", totalIncome]);

    const incomeSheet = XLSX.utils.aoa_to_sheet(incomeRows);
    XLSX.utils.book_append_sheet(workbook, incomeSheet, "Income");
  }

  // Expenses sheet
  if (data.expenses) {
    const expenseRows: (string | number)[][] = [["Category", "Monthly Amount"]];
    expenseRows.push(["Groceries", data.expenses.groceries || 0]);
    expenseRows.push(["Personal", data.expenses.personal || 0]);
    expenseRows.push(["Property (Owner Occupied)", data.expenses.propertyOwnerOccupied || 0]);
    expenseRows.push(["Property (Investment)", data.expenses.propertyInvestment || 0]);
    expenseRows.push(["Education", data.expenses.education || 0]);
    expenseRows.push(["Child Care", data.expenses.childCare || 0]);
    expenseRows.push(["Health", data.expenses.health || 0]);
    expenseRows.push(["Transport", data.expenses.transport || 0]);
    expenseRows.push(["Insurance", data.expenses.insurance || 0]);
    expenseRows.push(["Entertainment", data.expenses.entertainment || 0]);
    expenseRows.push(["Communication", data.expenses.communication || 0]);
    expenseRows.push(["Child Support", data.expenses.childSupport || 0]);
    expenseRows.push(["Other", data.expenses.other || 0]);
    expenseRows.push(["", ""]);
    expenseRows.push(["TOTAL MONTHLY", data.expenses.totalMonthly || 0]);

    const expenseSheet = XLSX.utils.aoa_to_sheet(expenseRows);
    XLSX.utils.book_append_sheet(workbook, expenseSheet, "Expenses");
  }

  // Requirements sheet
  if (data.requirements) {
    const reqRows: (string | number)[][] = [["Field", "Value"]];
    reqRows.push(["Primary Reason", data.requirements.primaryReason || ""]);
    reqRows.push(["Expected Contract Length", data.requirements.expectedContractLength || ""]);
    reqRows.push(["Seeking Interest Only", data.requirements.seekingInterestOnly ? "Yes" : "No"]);
    reqRows.push(["Concern About Rising Rates", data.requirements.concernAboutRisingRates || ""]);
    reqRows.push(["Has Defaults/Judgements", data.requirements.hasDefaultsOrJudgements ? "Yes" : "No"]);
    reqRows.push(["All Commitments Up to Date", data.requirements.allCommitmentsUpToDate ? "Yes" : "No"]);
    reqRows.push(["Expects Financial Changes", data.requirements.expectsFinancialChanges ? "Yes" : "No"]);
    reqRows.push(["Wants Financial Adviser Intro", data.requirements.wantsFinancialAdviserIntro ? "Yes" : "No"]);

    const features = Object.entries(data.requirements.desiredFeatures || {})
      .filter(([, v]) => v)
      .map(([k]) => k.replace(/([A-Z])/g, " $1").trim())
      .join(", ");
    reqRows.push(["Desired Features", features]);

    const reqSheet = XLSX.utils.aoa_to_sheet(reqRows);
    XLSX.utils.book_append_sheet(workbook, reqSheet, "Requirements");
  }

  const fileName = `FactFinder_${data.id.substring(0, 8)}_${formatDate(new Date().toISOString()).replace(/\//g, "-")}.xlsx`;
  XLSX.writeFile(workbook, fileName);
}
